import { useSnackbar } from 'notistack';
import { ChangeEvent, Dispatch, useEffect, useRef, useState } from 'react';
import { getAllConstructionList } from '../../../../apicalls/construction';
import { TConstructionMinimal, TSelectItem } from '../../../../types/common';
import {
  checkResData,
  formatDatetime,
  getConstructionsSelectBoxData
} from '../../../../utils/common';
import {
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue
} from 'react-hook-form';
import { convertFormDateToApiDate, log } from '../../../../utils';
import { parseNumber } from '../../../../utils/processAndTaskFormUtils';
import {
  MAX_IMAGE_COUNT,
  MAX_IMAGE_MB,
  MAX_IMAGE_SIZE
} from '../../../../const/DailyReportConst';
import { createDailyReport } from '../../../../apicalls/dailyReport';
import { useDropzone } from 'react-dropzone';
import { TAddDailyReportForm } from '../../../../types/dailyReport';
import { acceptablePictureContentTypes } from '../../../../utils/common';

export type Props = {
  setIsOpen: Dispatch<boolean>;
  afterSubmit: () => void;
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  reset: UseFormReset<any>;
  isOpen: boolean;
  constructIdDef?: number;
  photoSyncEnabled: boolean;
};

export const useHooks = (props: Props) => {
  const {
    setIsOpen,
    afterSubmit,
    getValues,
    setValue,
    reset,
    isOpen,
    constructIdDef,
    photoSyncEnabled
  } = props;

  // 送信可能フラグ 多重送信対策
  const isSubmitable = useRef(false);

  const [isSending, setIsSending] = useState(false);

  const submit = (form: TAddDailyReportForm) => {
    if (!isSubmitable.current) return;

    isSubmitable.current = false;

    setIsSending(true);
    createDailyReport(
      convertFormDateToApiDate(form.work_day) as string,
      form.construct_id.value,
      form.report_detail,
      photoSyncEnabled,
      files,
      form.cost_up_factor,
      form.cost_up_amount,
      form.cost_down_factor,
      form.cost_down_amount
    ).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          setIsOpen(false);
          afterSubmit();
        } else {
          isSubmitable.current = true;
        }
        setIsSending(false);
      });
    });
  };

  // 工事選択リスト
  const [constructList, setConstructList] = useState<TSelectItem[]>([]);

  // ファイルデータ
  const [files, setFiles] = useState<File[]>([]);

  // ファイル追加
  const addFile = (fileList: File[]) => {
    const fileCount: number = fileList.length;
    if (fileCount <= 0) {
      return;
    }
    const filesData: File[] = files.concat(fileList);

    // 枚数上限チェック
    if (filesData.length > MAX_IMAGE_COUNT) {
      enqueueSnackbar(`アップロードできる画像は${MAX_IMAGE_COUNT}枚までです`, {
        variant: 'error'
      });
      return;
    }

    setFiles(
      filesData.filter(file => {
        // ファイル形式チェック
        const format = file.type;
        if (!acceptablePictureContentTypes().includes(format)) {
          enqueueSnackbar(
            `保存可能なファイル形式ではありません [ ${file.name} ]`,
            {
              variant: 'error'
            }
          );
          return false;
        }
        // ファイルサイズチェック
        if (file.size > MAX_IMAGE_SIZE) {
          enqueueSnackbar(
            `ファイルサイズは${MAX_IMAGE_MB}MB以下にしてください [ ${file.name} ]`,
            {
              variant: 'error'
            }
          );
          return false;
        }
        return true;
      })
    );
  };

  // ファイル削除（取消）
  const deletePicture = (index: number) => {
    const newFiles: File[] = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  /**
   * ドラッグ&ドロップされた際の処理
   * @param acceptedFiles
   */
  const onDrop = (acceptedFiles: File[]) => {
    addFile(acceptedFiles);
  };

  // ドラッグ&ドロップの初期設定
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  // レスポンシブレイアウト時の画像取込み時の処理
  const handleCaptureImage = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (!files || files?.length === 0) return;
    const fileList: File[] = [];
    for (var i = 0; i < files.length; i++) {
      fileList.push(files[i]);
    }
    addFile(fileList);
  };

  // キャンセル時の処理
  const cancelSubmit = () => {
    setIsOpen(false);
  };

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isOpen) {
      // 入力の初期化
      reset({
        work_day: formatDatetime(
          new Date(new Date().toDateString()),
          'YYYY-MM-DD'
        ),
        construct_id: { value: 0, label: '--' },
        cost_up_factor: '',
        cost_up_amount: '',
        cost_down_factor: '',
        cost_down_amount: '',
        report_detail: ''
      });
      // 写真リストの初期化
      setFiles([]);

      // 工事セレクトボックスを取得
      getConstructionsSelectBoxData(setConstructList, enqueueSnackbar, {
        value: 0,
        label: '--'
      });
      // 工事選択リスト設定
      getAllConstructionList().then((resData: any) => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          if (data) {
            const resConstructList: TConstructionMinimal[] = data.data;
            if (resConstructList && resConstructList.length > 0) {
              const list: TSelectItem[] = [];
              list.push({ value: 0, label: '--' });
              resConstructList.forEach((construct: TConstructionMinimal) => {
                list.push({
                  value: construct.id,
                  label: construct.name ?? ''
                });
              });
              // 工事IDが渡されている場合は初期表示
              if (constructIdDef) {
                const defaultConstruct = list.find(construct => {
                  return construct.value === constructIdDef;
                });
                if (defaultConstruct) {
                  reset({ construct_id: defaultConstruct });
                }
              }
            }
          }
        });
      });

      // モーダル表示時にログ出力
      log.info('to day-report create modal: supervisor');

      isSubmitable.current = true;
    }
  }, [isOpen]);

  return {
    submit,
    cancelSubmit,
    constructList,
    files,
    addFile,
    deletePicture,
    getRootProps,
    getInputProps,
    handleCaptureImage,
    isSending
  };
};
